import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/context/AuthContext';

const DISCLAIMER_KEY = 'nauticoach_disclaimer_accepted';

const DisclaimerDialog = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      const accepted = localStorage.getItem(`${DISCLAIMER_KEY}_${user.id}`);
      if (!accepted) {
        setOpen(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!open) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasScrolledToBottom(true);
        }
      },
      { threshold: 1.0 }
    );

    // Small delay to let the dialog render
    const timer = setTimeout(() => {
      if (bottomRef.current) {
        observer.observe(bottomRef.current);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [open]);

  const handleAccept = () => {
    if (user) {
      localStorage.setItem(`${DISCLAIMER_KEY}_${user.id}`, 'true');
    }
    setOpen(false);
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-lg [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl text-center">
            ⚓ Limitation de responsabilité
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4" ref={scrollRef}>
          <div className="space-y-4 text-sm text-muted-foreground">
            <section>
              <h3 className="font-semibold text-foreground mb-1">1. Rôle de l'Assistant IA et de l'Application</h3>
              <p>
                L'Utilisateur reconnaît que NautiCoach est un outil numérique d'aide à la gestion, à l'inventaire et à la maintenance préventive. Les conseils, checklists, analyses météo ou diagnostics techniques générés par l'Intelligence Artificielle (IA) sont fournis à titre purement indicatif et ne constituent en aucun cas une expertise certifiée ou une obligation de résultat.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1">2. Responsabilité du "Chef de Bord"</h3>
              <p className="mb-2">
                Conformément aux réglementations maritimes en vigueur (notamment la Division 240 en France), l'Utilisateur conserve en toutes circonstances la qualité et les responsabilités de Chef de Bord. Il est seul responsable :
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>De la conduite du navire et de la sécurité de l'équipage.</li>
                <li>De la vérification visuelle et physique de l'état du navire (vannes de coque, gréement, moteur, équipements de sécurité).</li>
                <li>De la décision finale de prendre la mer ou d'effectuer une manœuvre.</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1">3. Exclusions spécifiques</h3>
              <p className="mb-2">La responsabilité de l'Éditeur (NautiCoach) ne pourra être engagée pour :</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Sinistres en mer :</strong> Naufrage, échouage, collision ou avarie moteur survenus suite à l'utilisation d'une checklist ou d'un conseil de l'IA.</li>
                <li><strong>Défaut de Maintenance :</strong> Oubli d'une tâche de maintenance, même si celle-ci n'a pas été notifiée par l'application.</li>
                <li><strong>Données Erronées :</strong> Erreurs provenant de sources tierces (données météo, capteurs IoT, informations constructeurs).</li>
                <li><strong>Dommages Indirects :</strong> Perte d'exploitation, frais de remorquage, frais de réparation ou perte de jouissance du navire.</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-foreground mb-1">4. Absence de garantie</h3>
              <p>
                L'Éditeur ne garantit pas que l'Application est exempte d'erreurs, de bugs ou d'interruptions de service. L'Utilisateur accepte d'utiliser le service "en l'état" et reconnaît que la navigation de plaisance comporte des risques intrinsèques que l'Application ne peut éliminer.
              </p>
            </section>

            <div ref={bottomRef} className="h-1" />
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            onClick={handleAccept}
            disabled={!hasScrolledToBottom}
            className="w-full"
          >
            {hasScrolledToBottom
              ? "J'accepte et je reconnais mon statut de Chef de Bord responsable"
              : "Veuillez lire l'intégralité du texte ↓"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisclaimerDialog;
